import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  HttpClientError,
  IQuerySettings,
  QueryKeys,
  getQuerySettings,
} from "@dw/models/shared";
import { useHttpClient, useSnackbar } from "@dw/hooks";
import {
  IApplyTermDateChangeRequest,
  ICreateDanceSchoolRequest,
  IDanceSchool,
  IPreviewTermDateChangeRequest,
  ISetCurrentEnrolmentYearRequest,
  ITermDateChangeReport,
  IUpdateDanceSchoolAppSettingsRequest,
  IUpdateDanceSchoolBillingSettingsRequest,
  IUpdateDanceSchoolInvoiceBrandingRequest,
  IUpdateDanceSchoolPublicInfoRequest,
  IUpdateDanceSchoolTermsAndConditionsRequest,
  IUpdateDanceSchoolXeroPaymentAccountRequest,
  IUpdateDanceSchoolXeroSalesAccountCodeRequest,
  IUpsertDanceSchoolAnnualSettingsRequest,
} from "@dw/models/danceSchool";
import { IStartDanceSchoolBillingYearRequest } from "@dw/models/dancerAccount";

const BASE_URL = "/v1/dance-schools";

enum DanceSchoolQueryKeys {
  DanceSchoolList = "DanceSchoolList",
}

export const useGetMyDanceSchools = (
  settings?: IQuerySettings<IDanceSchool[]>
) => {
  const { GET } = useHttpClient();

  const querySettings = getQuerySettings(settings);
  return useQuery<IDanceSchool[], HttpClientError>({
    queryKey: [QueryKeys.DanceSchools, DanceSchoolQueryKeys.DanceSchoolList],
    queryFn: () => {
      return GET<IDanceSchool[]>(`${BASE_URL}/my-schools`);
    },
    ...querySettings,
  });
};

export const useGetDanceSchool = (
  danceSchoolId: string,
  settings?: IQuerySettings<IDanceSchool>
) => {
  const { GET } = useHttpClient();

  const querySettings = getQuerySettings(settings);
  return useQuery<IDanceSchool, HttpClientError>({
    queryKey: [QueryKeys.DanceSchools, danceSchoolId],
    queryFn: () => {
      return GET<IDanceSchool>(`${BASE_URL}/${danceSchoolId}`);
    },
    ...querySettings,
    enabled: Boolean(danceSchoolId) && querySettings.enabled,
  });
};

export const useCreateDanceSchoolMutation = (
  settings?: IQuerySettings<IDanceSchool, ICreateDanceSchoolRequest>
) => {
  const { POST } = useHttpClient();
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  const createDanceSchool = async (request: ICreateDanceSchoolRequest) => {
    return POST<IDanceSchool>(`${BASE_URL}`, request);
  };

  const querySettings = getQuerySettings(settings);
  return useMutation<IDanceSchool, HttpClientError, ICreateDanceSchoolRequest>({
    ...querySettings,
    mutationFn: createDanceSchool,
    onSuccess: (response, input) => {
      showSnackbar("Successfully created dance school.");
      queryClient.invalidateQueries({
        queryKey: [
          QueryKeys.DanceSchools,
          DanceSchoolQueryKeys.DanceSchoolList,
        ],
      });
      queryClient.setQueryData([QueryKeys.DanceSchools, response.id], response);
      if (querySettings.onSuccess) {
        querySettings.onSuccess(response, input);
      }
    },
  });
};

export const useUpdateDanceSchoolPublicInfoMutation = (
  settings?: IQuerySettings<IDanceSchool, IUpdateDanceSchoolPublicInfoRequest>
) => {
  const { PUT } = useHttpClient();
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  const updateDanceSchoolPublicInfo = async (request: IUpdateDanceSchoolPublicInfoRequest) => {
    return PUT<IDanceSchool>(`${BASE_URL}/public-info`, request);
  };

  const querySettings = getQuerySettings(settings);
  return useMutation<IDanceSchool, HttpClientError, IUpdateDanceSchoolPublicInfoRequest>({
    ...querySettings,
    mutationFn: updateDanceSchoolPublicInfo,
    onSuccess: (response, input) => {
      showSnackbar("Successfully updated dance school.");
      queryClient.invalidateQueries({
        queryKey: [
          QueryKeys.DanceSchools,
          DanceSchoolQueryKeys.DanceSchoolList,
        ],
      });
      queryClient.setQueryData([QueryKeys.DanceSchools, response.id], response);
      if (querySettings.onSuccess) {
        querySettings.onSuccess(response, input);
      }
    },
  });
};

export const useUpdateDanceSchoolXeroSalesAccountCodeMutation = (
  settings?: IQuerySettings<IDanceSchool, IUpdateDanceSchoolXeroSalesAccountCodeRequest>
) => {
  const { PUT } = useHttpClient();
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  const updateDanceSchoolXeroSalesAccountCode = async (request: IUpdateDanceSchoolXeroSalesAccountCodeRequest) => {
    return PUT<IDanceSchool>(`${BASE_URL}/xero-sales-account-code`, request);
  };

  const querySettings = getQuerySettings(settings);
  return useMutation<IDanceSchool, HttpClientError, IUpdateDanceSchoolXeroSalesAccountCodeRequest>({
    ...querySettings,
    mutationFn: updateDanceSchoolXeroSalesAccountCode,
    onSuccess: (response, input) => {
      showSnackbar("Successfully updated Xero sales account code.");
      queryClient.invalidateQueries({
        queryKey: [
          QueryKeys.DanceSchools,
          DanceSchoolQueryKeys.DanceSchoolList,
        ],
      });
      queryClient.setQueryData([QueryKeys.DanceSchools, response.id], response);
      if (querySettings.onSuccess) {
        querySettings.onSuccess(response, input);
      }
    },
  });
};

export const useUpdateDanceSchoolXeroPaymentAccountMutation = (
  settings?: IQuerySettings<IDanceSchool, IUpdateDanceSchoolXeroPaymentAccountRequest>
) => {
  const { PUT } = useHttpClient();
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  const updateDanceSchoolXeroPaymentAccount = async (request: IUpdateDanceSchoolXeroPaymentAccountRequest) => {
    return PUT<IDanceSchool>(`${BASE_URL}/xero-payment-account`, request);
  };

  const querySettings = getQuerySettings(settings);
  return useMutation<IDanceSchool, HttpClientError, IUpdateDanceSchoolXeroPaymentAccountRequest>({
    ...querySettings,
    mutationFn: updateDanceSchoolXeroPaymentAccount,
    onSuccess: (response, input) => {
      showSnackbar("Successfully updated Xero payment account.");
      queryClient.invalidateQueries({
        queryKey: [
          QueryKeys.DanceSchools,
          DanceSchoolQueryKeys.DanceSchoolList,
        ],
      });
      queryClient.setQueryData([QueryKeys.DanceSchools, response.id], response);
      if (querySettings.onSuccess) {
        querySettings.onSuccess(response, input);
      }
    },
  });
};

/**
 * Saves the school's Dance World app settings — today, the appearance it pins for its dancers.
 *
 * Writes the returned school straight into the dance-school query cache, which is what the Auth
 * context reads, so the preview and every other consumer of `useAuth().danceSchool` update without
 * a refetch.
 */
export const useUpdateDanceSchoolAppSettingsMutation = (
  settings?: IQuerySettings<IDanceSchool, IUpdateDanceSchoolAppSettingsRequest>
) => {
  const { PUT } = useHttpClient();
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  const updateDanceSchoolAppSettings = async (request: IUpdateDanceSchoolAppSettingsRequest) => {
    return PUT<IDanceSchool>(`${BASE_URL}/app-settings`, request);
  };

  const querySettings = getQuerySettings(settings);
  return useMutation<IDanceSchool, HttpClientError, IUpdateDanceSchoolAppSettingsRequest>({
    ...querySettings,
    mutationFn: updateDanceSchoolAppSettings,
    onSuccess: (response, input) => {
      showSnackbar("Successfully updated app settings.");
      queryClient.invalidateQueries({
        queryKey: [
          QueryKeys.DanceSchools,
          DanceSchoolQueryKeys.DanceSchoolList,
        ],
      });
      queryClient.setQueryData([QueryKeys.DanceSchools, response.id], response);
      if (querySettings.onSuccess) {
        querySettings.onSuccess(response, input);
      }
    },
  });
};

export const useUpdateDanceSchoolBillingSettingsMutation = (
  settings?: IQuerySettings<IDanceSchool, IUpdateDanceSchoolBillingSettingsRequest>
) => {
  const { PUT } = useHttpClient();
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  const updateDanceSchoolBillingSettings = async (request: IUpdateDanceSchoolBillingSettingsRequest) => {
    return PUT<IDanceSchool>(`${BASE_URL}/billing-settings`, request);
  };

  const querySettings = getQuerySettings(settings);
  return useMutation<IDanceSchool, HttpClientError, IUpdateDanceSchoolBillingSettingsRequest>({
    ...querySettings,
    mutationFn: updateDanceSchoolBillingSettings,
    onSuccess: (response, input) => {
      showSnackbar("Successfully updated billing settings.");
      queryClient.invalidateQueries({
        queryKey: [
          QueryKeys.DanceSchools,
          DanceSchoolQueryKeys.DanceSchoolList,
        ],
      });
      queryClient.setQueryData([QueryKeys.DanceSchools, response.id], response);
      if (querySettings.onSuccess) {
        querySettings.onSuccess(response, input);
      }
    },
  });
};

export const useUpdateDanceSchoolInvoiceBrandingMutation = (
  settings?: IQuerySettings<IDanceSchool, IUpdateDanceSchoolInvoiceBrandingRequest>
) => {
  const { PUT } = useHttpClient();
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  const updateDanceSchoolInvoiceBranding = async (request: IUpdateDanceSchoolInvoiceBrandingRequest) => {
    return PUT<IDanceSchool>(`${BASE_URL}/invoice-branding`, request);
  };

  const querySettings = getQuerySettings(settings);
  return useMutation<IDanceSchool, HttpClientError, IUpdateDanceSchoolInvoiceBrandingRequest>({
    ...querySettings,
    mutationFn: updateDanceSchoolInvoiceBranding,
    onSuccess: (response, input) => {
      showSnackbar("Successfully updated invoice branding.");
      queryClient.invalidateQueries({
        queryKey: [
          QueryKeys.DanceSchools,
          DanceSchoolQueryKeys.DanceSchoolList,
        ],
      });
      queryClient.setQueryData([QueryKeys.DanceSchools, response.id], response);
      if (querySettings.onSuccess) {
        querySettings.onSuccess(response, input);
      }
    },
  });
};

export const useUpdateDanceSchoolTermsAndConditionsMutation = (
  settings?: IQuerySettings<IDanceSchool, IUpdateDanceSchoolTermsAndConditionsRequest>
) => {
  const { POST } = useHttpClient();
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  const updateDanceSchoolTermsAndConditions = async (request: IUpdateDanceSchoolTermsAndConditionsRequest) => {
    return POST<IDanceSchool>(`${BASE_URL}/terms-and-conditions`, request);
  };

  const querySettings = getQuerySettings(settings);
  return useMutation<IDanceSchool, HttpClientError, IUpdateDanceSchoolTermsAndConditionsRequest>({
    ...querySettings,
    mutationFn: updateDanceSchoolTermsAndConditions,
    onSuccess: (response, input) => {
      showSnackbar("Successfully updated terms and conditions.");
      queryClient.invalidateQueries({
        queryKey: [
          QueryKeys.DanceSchools,
          DanceSchoolQueryKeys.DanceSchoolList,
        ],
      });
      queryClient.setQueryData([QueryKeys.DanceSchools, response.id], response);
      if (querySettings.onSuccess) {
        querySettings.onSuccess(response, input);
      }
    },
  });
};

export const useUpsertDanceSchoolAnnualSettingsMutation = (
  settings?: IQuerySettings<IDanceSchool, IUpsertDanceSchoolAnnualSettingsRequest>
) => {
  const { PUT } = useHttpClient();
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  const upsertDanceSchoolAnnualSettings = async (request: IUpsertDanceSchoolAnnualSettingsRequest) => {
    return PUT<IDanceSchool>(`${BASE_URL}/annual-settings`, request);
  };

  const querySettings = getQuerySettings(settings);
  return useMutation<IDanceSchool, HttpClientError, IUpsertDanceSchoolAnnualSettingsRequest>({
    ...querySettings,
    mutationFn: upsertDanceSchoolAnnualSettings,
    onSuccess: (response, input) => {
      showSnackbar("Successfully updated dance school settings.");
      queryClient.setQueryData([QueryKeys.DanceSchools, response.id], response);
      if (querySettings.onSuccess) {
        querySettings.onSuccess(response, input);
      }
    },
  });
};

export const useStartDanceSchoolBillingYearMutation = (
  settings?: IQuerySettings<IDanceSchool, IStartDanceSchoolBillingYearRequest>
) => {
  const { POST } = useHttpClient();
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  const startDanceSchoolBillingYear = async (request: IStartDanceSchoolBillingYearRequest) => {
    return POST<IDanceSchool>(`${BASE_URL}/annual-settings/start-year`, request);
  };

  const querySettings = getQuerySettings(settings);
  return useMutation<IDanceSchool, HttpClientError, IStartDanceSchoolBillingYearRequest>({
    ...querySettings,
    mutationFn: startDanceSchoolBillingYear,
    onSuccess: (response, input) => {
      showSnackbar(`Successfully opened ${input.year} enrolments.`);
      queryClient.setQueryData([QueryKeys.DanceSchools, response.id], response);
      if (querySettings.onSuccess) {
        querySettings.onSuccess(response, input);
      }
    },
  });
};

export const useSetCurrentEnrolmentYearMutation = (
  settings?: IQuerySettings<IDanceSchool, ISetCurrentEnrolmentYearRequest>
) => {
  const { POST } = useHttpClient();
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  const setCurrentEnrolmentYear = async (request: ISetCurrentEnrolmentYearRequest) => {
    return POST<IDanceSchool>(`${BASE_URL}/annual-settings/set-current-year`, request);
  };

  const querySettings = getQuerySettings(settings);
  return useMutation<IDanceSchool, HttpClientError, ISetCurrentEnrolmentYearRequest>({
    ...querySettings,
    mutationFn: setCurrentEnrolmentYear,
    onSuccess: (response, input) => {
      showSnackbar(`Successfully set ${input.year} as the current enrolment year.`);
      queryClient.setQueryData([QueryKeys.DanceSchools, response.id], response);
      if (querySettings.onSuccess) {
        querySettings.onSuccess(response, input);
      }
    },
  });
};

export const usePreviewTermDateChangeMutation = (
  settings?: IQuerySettings<ITermDateChangeReport, IPreviewTermDateChangeRequest>
) => {
  const { POST } = useHttpClient();

  const previewTermDateChange = async (request: IPreviewTermDateChangeRequest) => {
    return POST<ITermDateChangeReport>(`${BASE_URL}/terms/change-preview`, request);
  };

  const querySettings = getQuerySettings(settings);
  return useMutation<ITermDateChangeReport, HttpClientError, IPreviewTermDateChangeRequest>({
    ...querySettings,
    mutationFn: previewTermDateChange,
    onSuccess: (response, input) => {
      // Intentionally no cache writes and no snackbar - the caller renders the returned report.
      if (querySettings.onSuccess) {
        querySettings.onSuccess(response, input);
      }
    },
  });
};

export const useApplyTermDateChangeMutation = (
  danceSchoolId: string,
  settings?: IQuerySettings<ITermDateChangeReport, IApplyTermDateChangeRequest>
) => {
  const { POST } = useHttpClient();
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  const applyTermDateChange = async (request: IApplyTermDateChangeRequest) => {
    return POST<ITermDateChangeReport>(`${BASE_URL}/terms/apply`, request);
  };

  const querySettings = getQuerySettings(settings);
  return useMutation<ITermDateChangeReport, HttpClientError, IApplyTermDateChangeRequest>({
    ...querySettings,
    mutationFn: applyTermDateChange,
    onSuccess: (response, input) => {
      showSnackbar("Term dates updated.");
      // The applied change re-derives the active school's stored terms/settings - refetch it.
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.DanceSchools, danceSchoolId],
      });
      if (querySettings.onSuccess) {
        querySettings.onSuccess(response, input);
      }
    },
  });
};
